import React, { useState, useEffect } from 'react';
import { Calendar, Search, Download, Users, DollarSign, FileText, CheckCircle2 } from 'lucide-react';
import { apiFetch } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './Relatorios.css';

const hojeMesAno = () => {
  const hoje = new Date();
  return `${String(hoje.getMonth() + 1).padStart(2, '0')}/${hoje.getFullYear()}`;
};

const mesInputParaApi = (valorInput) => {
  // <input type="month"> devolve "AAAA-MM" — a API espera "MM/AAAA"
  if (!valorInput) return '';
  const [ano, mes] = valorInput.split('-');
  return `${mes}/${ano}`;
};

const mesApiParaInput = (mesApi) => {
  // "MM/AAAA" -> "AAAA-MM", pro valor inicial do <input type="month">
  const [mes, ano] = mesApi.split('/');
  return `${ano}-${mes}`;
};

const mesInputParaPt = (valor) => {
  const match = String(valor || '').match(/^(\d{4})-(\d{2})$/);
  if (!match) return 'Selecione o mês';
  const data = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  const texto = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(data);
  return texto.charAt(0).toLocaleUpperCase('pt-BR') + texto.slice(1);
};

function exportCSV(linhas, nomeArquivo) {
  const csv = linhas.map((linha) =>
    linha.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';')
  ).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeArquivo;
  a.click();
  URL.revokeObjectURL(url);
}

// ENTRADA-PAGAMENTO: aba só para admin e gestor: quem entrou num mês (e decêndio) e se pagou no mês escolhido
const SITUACOES_ENTRADA = {
  sim: { rotulo: 'Pagou', bg: '#ecfdf5', cor: '#047857' },
  parte: { rotulo: 'Pagou em parte', bg: '#fffbeb', cor: '#b45309' },
  nao: { rotulo: 'Não pagou', bg: '#fef2f2', cor: '#e11d48' },
};
const fmtDiaBr = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};
const brlEntrada = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// RECEBIDO-MES: aba só para admin e gestor: tudo que a CPFL recebeu no mês (pela data do pagamento) e quem pagou, direto dos arquivos de retorno

const mesRecebidoPt = (valor) => {
  const match = String(valor || '').match(/^(\d{4})-(\d{2})$/);
  if (!match) return 'Selecione o mês';
  const data = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  const texto = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(data);
  return texto.charAt(0).toLocaleUpperCase('pt-BR') + texto.slice(1);
};

function RecebidoMes({ aba, setAba }) {
  const [mes, setMes] = useState(mesApiParaInput(hojeMesAno()));
  const [filtro, setFiltro] = useState('todos');
  const [busca, setBusca] = useState('');
  const [decendio, setDecendio] = useState('todos');   // RECEBIDO-DECENDIO
  const hojeIso = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [res, setRes] = useState(null);

  const consultar = async (decEscolhido) => {
    const dec = typeof decEscolhido === 'string' ? decEscolhido : decendio;
    const m = mesInputParaApi(mes);
    if (!m) return;
    setLoading(true);
    setErro(null);
    try {
      const r = await apiFetch(`/cpfl/relatorio/recebido-mes?mes=${encodeURIComponent(m)}&decendio=${dec}`);
      setRes(r);
      setFiltro('todos');
      setBusca('');
    } catch (err) {
      setErro(err.message || 'Erro ao consultar');
      setRes(null);
    } finally {
      setLoading(false);
    }
  };
  // abre já mostrando o mês escolhido (começa no mês atual)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { consultar(); }, []);

  const norm = (s) => String(s || '').toLowerCase();
  const termo = norm(busca.trim());
      const linhas = res
      ? (filtro === 'irregularidade'
          ? (res.irregularidades || []).filter((d) => !termo || [d.nome, d.cpf, d.instalacao, d.identificacoes, d.situacoes, (d.codigo_irregularidades || []).join(' ')].some((x) => norm(x).includes(termo)))
          : res.dados.filter((d) => (filtro === 'todos' || (filtro === 'cad' ? d.cadastrado : !d.cadastrado))
              && (!termo || [d.nome, d.cpf, d.instalacao, d.identificacoes].some((x) => norm(x).includes(termo))))
        )
      : [];

  return (
    <>
        <div className="rel-detalhe-header cpfl-recebido-header">
          <div className="rel-detalhe-title-area amber">
            <span className="rel-detalhe-icon">⚡</span>
            <div>
              <h1>Recebido no mês — CPFL</h1>
              <p className="page-subtitle">Tudo que a CPFL recebeu no mês (pela data do pagamento) e quem pagou, direto dos arquivos de retorno</p>
            </div>
          </div>
          {linhas.length > 0 && (
            <button
              className="btn-export cpfl-recebido-export"
              type="button"
              onClick={() => exportCSV(
                filtro === 'irregularidade'
                  ? [['Cliente', 'CPF', 'Instalação', 'Situação CPFL', 'Código(s)'], ...linhas.map((d) => [d.nome, d.cpf || '', d.instalacao || d.identificacoes || '', d.situacoes, (d.codigo_irregularidades || []).join(', ')])]
                  : [['Cliente', 'CPF', 'Instalação', 'Identificação(ões)', 'Pagamentos', 'Valor', 'Primeiro pagamento', 'Último pagamento', 'Cadastro no sistema'],
                     ...linhas.map((d) => [d.nome || '(sem cadastro no sistema)', d.cpf || '', d.instalacao || '', d.identificacoes, d.pagamentos, d.valor, fmtDiaBr(d.primeiro), fmtDiaBr(d.ultimo), d.cadastrado ? 'Sim' : 'Não'])]`recebido-${res.mes.replace('/', '-')}${res.decendio && res.decendio !== 'todos' ? `-dec${res.decendio}` : ''}.csv`
              )}
            >
              <Download size={14} /> Exportar CSV
            </button>
          )}
        </div>
        <AbasPagantes aba={aba} setAba={setAba} />

      <div className="rel-filters cpfl-recebido-controls">
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#64748b' }}>
          Mês do pagamento
          <div className="cpfl-recebido-month-picker"><span><Calendar size={15} aria-hidden="true" />{mesRecebidoPt(mes)}</span><input type="month" lang="pt-BR" aria-label="Mês do pagamento" value={mes} onChange={(e) => setMes(e.target.value)} /></div>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#64748b' }}>
          Decêndio
          <select value={decendio} onChange={(e) => { setDecendio(e.target.value); consultar(e.target.value); }} className="filter-input">
            <option value="todos">Todos</option>
            <option value="1">1º (dias 1 a 10)</option>
            <option value="2">2º (dias 11 a 20)</option>
            <option value="3">3º (dia 21 ao fim)</option>
          </select>
        </label>
        <button
          type="button"
          onClick={consultar}
          disabled={loading}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, alignSelf: 'flex-end',
            padding: '10px 20px', borderRadius: 8, border: 'none',
            background: '#d97706', color: '#fff', fontWeight: 700, fontSize: 14,
            cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1,
          }}
        >
          <Search size={16} /> {loading ? 'Consultando...' : 'Consultar'}
        </button>
        
      </div>

      {erro && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#e11d48', padding: '12px 16px', borderRadius: 8, marginBottom: 16 }}>
          {erro}
        </div>
      )}

      {res && !erro && (
        <>
          <div className="detalhe-summary-grid">
            <div className="detalhe-summary-card green">
              <div className="summary-label"><DollarSign size={12} style={{ marginRight: 4 }} />Valor recebido em {res.mes}{res.decendio && res.decendio !== 'todos' ? ` · ${res.decendio}º decêndio` : ''}</div>
              <div className="summary-value">{brlEntrada(res.totalRecebido)}</div>
            </div>
            <div className="detalhe-summary-card blue">
              <div className="summary-label"><Users size={12} style={{ marginRight: 4 }} />Clientes que pagaram</div>
              <div className="summary-value">{res.clientesPagaram}</div>
            </div>
            <div className="detalhe-summary-card amber">
              <div className="summary-label"><FileText size={12} style={{ marginRight: 4 }} />Pagamentos</div>
              <div className="summary-value">{res.pagamentos}</div>
            </div>
                          <div className="detalhe-summary-card amber">
                <div className="summary-label"><Calendar size={12} style={{ marginRight: 4 }} />Sem cadastro no sistema</div>
                <div className="summary-value">{res.semCadastro.clientes} · {brlEntrada(res.semCadastro.valor)}</div>
              </div>
              <div className="detalhe-summary-card rose cpfl-irregularidade-card">
                <div className="summary-label"><Users size={12} style={{ marginRight: 4 }} />Clientes (Irregularidade)</div>
                <div className="summary-value">{(res.irregularidades || []).length}</div>
              </div>
          </div>

          <div style={{ margin: '16px 0 6px', fontSize: 12, fontWeight: 700, color: '#64748b' }}>
            Por decêndio, com a data de repasse do Calendário da CPFL (clique em um para ver só quem pagou nele)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 10, marginBottom: 6 }}>
            {(res.porDecendio || []).map((p) => {
              const ativo = String(p.decendio) === res.decendio;
              const emAndamento = p.fim >= hojeIso;
              const rep = !p.repasse ? 'Sem data de repasse no calendário'
                : p.repasse < hojeIso ? `Repasse: ${fmtDiaBr(p.repasse)} (a data já passou)`
                : p.repasse === hojeIso ? `Repasse: hoje (${fmtDiaBr(p.repasse)})` : `Repasse previsto: ${fmtDiaBr(p.repasse)}`;
              return (
                <button
                  key={p.decendio}
                  type="button"
                  onClick={() => { const novo = ativo ? 'todos' : String(p.decendio); setDecendio(novo); consultar(novo); }}
                  style={{ textAlign: 'left', padding: 12, borderRadius: 10, cursor: 'pointer', border: ativo ? '2px solid #2563eb' : '1px solid #e2e8f0', background: ativo ? '#eff6ff' : '#fff' }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>{p.decendio}º decêndio · {fmtDiaBr(p.inicio).slice(0, 5)} a {fmtDiaBr(p.fim).slice(0, 5)}{emAndamento ? ' · em andamento' : ''}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#047857', margin: '4px 0' }}>{brlEntrada(p.valor)}</div>
                  <div style={{ fontSize: 12, color: '#475569' }}>{p.clientes} cliente(s) · {p.pagamentos} pagamento(s)</div>
                  <div style={{ fontSize: 11, color: '#b45309', marginTop: 4 }}>{rep}</div>
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 6, margin: '12px 0', flexWrap: 'wrap', alignItems: 'center' }}>
            {[{ id: 'todos', rotulo: `Todos (${res.clientesPagaram})` }, { id: 'cad', rotulo: `Cadastrados (${res.cadastrados.clientes})` }, { id: 'sem', rotulo: `Sem cadastro (${res.semCadastro.clientes})` }, { id: 'irregularidade', rotulo: `Irregularidade (${(res.irregularidades || []).length})` }].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFiltro(f.id)}
                style={{
                  padding: '6px 12px', borderRadius: 999, fontWeight: 600, fontSize: 12, cursor: 'pointer',
                  border: filtro === f.id ? '1px solid #2563eb' : '1px solid #e2e8f0',
                  background: filtro === f.id ? '#eff6ff' : '#fff',
                  color: filtro === f.id ? '#2563eb' : '#64748b',
                }}
              >
                {f.rotulo}
              </button>
            ))}
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome, CPF ou instalação"
              className="filter-input"
              style={{ minWidth: 260 }}
            />
          </div>

          <div className="table-wrapper">
            <div className="table-head-title">
              {filtro === 'irregularidade' ? 'Clientes com situação diferente de Pago' : 'Quem pagou em'} <span style={{ color: '#d97706' }}>{res.mes}</span>{res.decendio && res.decendio !== 'todos' && filtro !== 'irregularidade' ? ` · ${res.decendio}º decêndio` : ''} · {linhas.length} cliente(s) na lista
            </div>
            <table className="rel-table cpfl-recebido-table">
              <thead>
                {filtro === 'irregularidade' ? (
                  <tr><th>Cliente</th><th>CPF</th><th>Instalação</th><th>Situação CPFL</th><th>Código(s)</th></tr>
                ) : (
                  <tr>
                    <th>Cliente</th><th>CPF</th><th>Instalação</th><th>Pagamentos</th><th>Valor</th><th>Primeiro pagamento</th><th>Último pagamento</th>
                  </tr>
                )}
              </thead>
              <tbody>
                                  {linhas.map((d, i) => filtro === 'irregularidade' ? (
                    <tr key={d.cliente_chave || `${d.instalacao}-${i}`} className={i % 2 === 0 ? 'row-even' : 'row-odd'}>
                      <td className="td-client">{d.nome || '(sem nome cadastrado)'}</td>
                      <td>{d.cpf || '—'}</td>
                      <td>{d.instalacao || d.identificacoes || '—'}</td>
                      <td>{d.situacoes || 'Não Pago'}</td>
                      <td>{(d.codigo_irregularidades || []).join(', ') || '—'}</td>
                    </tr>
                  ) : (
                    <tr key={d.chave} className={i % 2 === 0 ? 'row-even' : 'row-odd'}>
                      <td className="td-client">
                        {d.cadastrado ? d.nome : <span style={{ color: '#b45309', fontStyle: 'italic' }}>Sem cadastro no sistema</span>}
                        {!d.cadastrado && <div style={{ fontSize: 11, color: '#94a3b8' }}>Identificação: {d.identificacoes}</div>}
                      </td>
                      <td>{d.cpf || '—'}</td><td>{d.instalacao || '—'}</td><td>{d.pagamentos}</td>
                      <td className="td-amount">{brlEntrada(d.valor)}</td><td className="td-date">{fmtDiaBr(d.primeiro)}</td><td className="td-date">{fmtDiaBr(d.ultimo)}</td>
                    </tr>
                  ))}
                  {linhas.length === 0 && (
                    <tr><td colSpan={filtro === 'irregularidade' ? 5 : 7} className="empty-row">Nenhum cliente nesse filtro</td></tr>
                  )}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 10 }}>
            Valores dos arquivos de retorno da CPFL (a mesma fonte do relatório de arrecadação do GMP), pela data do pagamento. Cliente = instalação.
            "Sem cadastro no sistema" são pagamentos de cobranças que ainda não existem aqui: o valor é recebido, só falta cadastrar o cliente.
            Decêndios: 1º = dias 1 a 10, 2º = dias 11 a 20 e 3º = dia 21 ao fim do mês. As datas de repasse são as do Calendário de Repasse da CPFL; o valor repassado pode vir com tarifas descontadas.
          </p>
        </>
      )}

      {!res && loading && !erro && (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
          <p>Consultando...</p>
        </div>
      )}
    </>
  );
}

function AbasPagantes({ aba, setAba }) {
  const abas = [{ id: 'pagantes', rotulo: 'Pagantes do mês' }, { id: 'recebido', rotulo: 'Recebido no mês' }, { id: 'entrada', rotulo: 'Por data de entrada' }];
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
      {abas.map((a) => (
        <button
          key={a.id}
          type="button"
          onClick={() => setAba(a.id)}
          style={{
            padding: '9px 16px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer',
            border: aba === a.id ? '1px solid #d97706' : '1px solid #e2e8f0',
            background: aba === a.id ? '#fffbeb' : '#fff',
            color: aba === a.id ? '#d97706' : '#64748b',
          }}
        >
          {a.rotulo}
        </button>
      ))}
    </div>
  );
}

function EntradaPagamento() {
  const [entrada, setEntrada] = useState(mesApiParaInput(hojeMesAno()));
  const [decendio, setDecendio] = useState('todos');
  const [pagamento, setPagamento] = useState(mesApiParaInput(hojeMesAno()));
  const [filtro, setFiltro] = useState('todos');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [res, setRes] = useState(null);

  const consultar = async () => {
    const e = mesInputParaApi(entrada);
    const p = mesInputParaApi(pagamento);
    if (!e || !p) return;
    setLoading(true);
    setErro(null);
    try {
      const r = await apiFetch(`/cpfl/relatorio/entrada-pagamento?entrada=${encodeURIComponent(e)}&decendio=${decendio}&pagamento=${encodeURIComponent(p)}`);
      setRes(r);
      setFiltro('todos');
    } catch (err) {
      setErro(err.message || 'Erro ao consultar');
      setRes(null);
    } finally {
      setLoading(false);
    }
  };

  const linhas = res ? res.dados.filter((d) => filtro === 'todos' || d.situacao === filtro) : [];
  const campo = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#64748b' };

  return (
    <>
      <div className="rel-filters">
        <label style={campo}>
          Entrou em
          <input type="month" value={entrada} onChange={(e) => setEntrada(e.target.value)} className="filter-input" />
        </label>
        <label style={campo}>
          Decêndio
          <select value={decendio} onChange={(e) => setDecendio(e.target.value)} className="filter-input">
            <option value="todos">Todos</option>
            <option value="1">1º (dias 1 a 10)</option>
            <option value="2">2º (dias 11 a 20)</option>
            <option value="3">3º (dia 21 ao fim)</option>
          </select>
        </label>
        <label style={campo}>
          Pagamento em
          <input type="month" value={pagamento} onChange={(e) => setPagamento(e.target.value)} className="filter-input" />
        </label>
        <button
          type="button"
          onClick={consultar}
          disabled={loading}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, alignSelf: 'flex-end',
            padding: '10px 20px', borderRadius: 8, border: 'none',
            background: '#d97706', color: '#fff', fontWeight: 700, fontSize: 14,
            cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1,
          }}
        >
          <Search size={16} /> {loading ? 'Consultando...' : 'Consultar'}
        </button>
        {linhas.length > 0 && (
          <button
            className="btn-export"
            type="button"
            onClick={() => exportCSV(
              [['Cliente', 'CPF', 'Instalação', 'Entrou em', 'Origem da data', 'Decêndio', 'Valor esperado', 'Pago no mês', 'Situação', 'Último pagamento', 'Status do cliente'],
               ...linhas.map((d) => [d.nome, d.cpf, d.instalacao, fmtDiaBr(d.entrada), d.origemEntrada, `${d.decendio}º`, d.esperado, d.pago, SITUACOES_ENTRADA[d.situacao].rotulo, fmtDiaBr(d.ultimoPagamento), d.statusCliente])],
              `entrada-${res.filtros.entrada.replace('/', '-')}-pagamento-${res.filtros.pagamento.replace('/', '-')}.csv`
            )}
          >
            <Download size={14} /> Exportar CSV
          </button>
        )}
      </div>

      {erro && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#e11d48', padding: '12px 16px', borderRadius: 8, marginBottom: 16 }}>
          {erro}
        </div>
      )}

      {res && !erro && (
        <>
          <div className="detalhe-summary-grid">
            <div className="detalhe-summary-card blue">
              <div className="summary-label"><Users size={12} style={{ marginRight: 4 }} />Entraram</div>
              <div className="summary-value">{res.resumo.entraram}</div>
            </div>
            <div className="detalhe-summary-card green">
              <div className="summary-label"><CheckCircle2 size={12} style={{ marginRight: 4 }} />Pagaram</div>
              <div className="summary-value">{res.resumo.pagaram}</div>
            </div>
            <div className="detalhe-summary-card amber">
              <div className="summary-label"><FileText size={12} style={{ marginRight: 4 }} />Pagaram em parte</div>
              <div className="summary-value">{res.resumo.emParte}</div>
            </div>
            <div className="detalhe-summary-card blue">
              <div className="summary-label"><Calendar size={12} style={{ marginRight: 4 }} />Não pagaram</div>
              <div className="summary-value">{res.resumo.naoPagaram}</div>
            </div>
            <div className="detalhe-summary-card green">
              <div className="summary-label"><DollarSign size={12} style={{ marginRight: 4 }} />Total pago no mês</div>
              <div className="summary-value">{brlEntrada(res.resumo.valorPago)}</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 6, margin: '12px 0' }}>
            {[{ id: 'todos', rotulo: 'Todos' }, { id: 'sim', rotulo: 'Pagou' }, { id: 'parte', rotulo: 'Pagou em parte' }, { id: 'nao', rotulo: 'Não pagou' }].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFiltro(f.id)}
                style={{
                  padding: '6px 12px', borderRadius: 999, fontWeight: 600, fontSize: 12, cursor: 'pointer',
                  border: filtro === f.id ? '1px solid #2563eb' : '1px solid #e2e8f0',
                  background: filtro === f.id ? '#eff6ff' : '#fff',
                  color: filtro === f.id ? '#2563eb' : '#64748b',
                }}
              >
                {f.rotulo}
              </button>
            ))}
          </div>

          <div className="table-wrapper">
            <div className="table-head-title">
              Clientes que entraram em <span style={{ color: '#d97706' }}>{res.filtros.entrada}</span>
              {res.filtros.decendio !== 'todos' ? ` (${res.filtros.decendio}º decêndio)` : ''} · pagamento em {res.filtros.pagamento}
            </div>
            <table className="rel-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Instalação</th>
                  <th>Entrou em</th>
                  <th>Esperado</th>
                  <th>Pago no mês</th>
                  <th>Situação</th>
                  <th>Último pagamento</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((d, i) => {
                  const sit = SITUACOES_ENTRADA[d.situacao];
                  return (
                    <tr key={d.clienteId} className={i % 2 === 0 ? 'row-even' : 'row-odd'}>
                      <td className="td-client">{d.nome}</td>
                      <td>{d.instalacao}</td>
                      <td className="td-date">
                        {fmtDiaBr(d.entrada)} · {d.decendio}º dec.
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{d.origemEntrada}</div>
                      </td>
                      <td className="td-amount">{brlEntrada(d.esperado)}</td>
                      <td className="td-amount">{brlEntrada(d.pago)}</td>
                      <td><span style={{ background: sit.bg, color: sit.cor, padding: '3px 10px', borderRadius: 999, fontWeight: 700, fontSize: 12 }}>{sit.rotulo}</span></td>
                      <td className="td-date">{fmtDiaBr(d.ultimoPagamento)}</td>
                    </tr>
                  );
                })}
                {linhas.length === 0 && (
                  <tr><td colSpan={7} className="empty-row">Nenhum cliente nesse filtro</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 10 }}>
            "Entrou em" é a data de abertura (quando existir) ou a da primeira cobrança do cliente. "Não pagou" pode ser um mês em que a CPFL não gerou conta
            (consumo abaixo do mínimo): confira o último pagamento. "Esperado" soma as cobranças ativas do cliente.
          </p>
        </>
      )}

      {!res && !loading && !erro && (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
          <Calendar size={40} style={{ marginBottom: 12, opacity: 0.5 }} />
          <p>Escolha o mês de entrada (e, se quiser, o decêndio) e clique em "Consultar".</p>
        </div>
      )}
    </>
  );
}

export default function PagantesMes() {
  const [mesInput, setMesInput] = useState(mesApiParaInput(hojeMesAno()));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resultado, setResultado] = useState(null);
  // ENTRADA-PAGAMENTO: a aba "Por data de entrada" só aparece para admin e gestor
  const { user } = useAuth();
  const podeVerEntrada = (user?.roles || []).some((r) => String(r).toLowerCase().includes('admin') || String(r).toLowerCase().includes('gestor'));
  const [aba, setAba] = useState('pagantes');

  const [modoTodos, setModoTodos] = useState(false);
  // CRITERIO-PAGAMENTO: 'pagamento' = pela DATA em que o dinheiro entrou (como a arrecadação da CPFL); 'referencia' = pelo mês da fatura paga
  const [criterio, setCriterio] = useState('pagamento');

  const consultar = async () => {
    const mes = mesInputParaApi(mesInput);
    if (!modoTodos && !mes) return;
    setLoading(true);
    setError(null);
    try {
      const base = criterio === 'pagamento' ? '/cpfl/relatorio/pagantes-mes-real' : '/cpfl/relatorio/pagantes-mes';
      const url = modoTodos ? base : `${base}?mes=${encodeURIComponent(mes)}`;
      const r = await apiFetch(url);
      setResultado({ ...r, criterio });
    } catch (err) {
      setError(err.message || 'Erro ao consultar pagantes do mês');
      setResultado(null);
    } finally {
      setLoading(false);
    }
  };

  const formatarValor = (v) =>
    (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const fmtData = (iso) => {
    const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : (iso || '—');
  };

    if (aba === 'recebido' && podeVerEntrada) {
      return (
        <div className="relatorios-page cpfl-recebido-page">
          <RecebidoMes aba={aba} setAba={setAba} />
        </div>
      );
    }

  if (aba === 'entrada' && podeVerEntrada) {
    return (
      <div className="relatorios-page">
        <div className="rel-detalhe-header">
          <div className="rel-detalhe-title-area amber">
            <span className="rel-detalhe-icon">⚡</span>
            <div>
              <h1>Entrada × Pagamento — CPFL</h1>
              <p className="page-subtitle">Quem entrou num mês (e decêndio) e se pagou no mês escolhido</p>
            </div>
          </div>
        </div>
        <AbasPagantes aba={aba} setAba={setAba} />
        <EntradaPagamento />
      </div>
    );
  }

  return (
    <div className="relatorios-page cpfl-pagantes-page">
      <div className="rel-detalhe-header cpfl-pagantes-header">
        <div className="rel-detalhe-title-area amber">
          <span className="rel-detalhe-icon">⚡</span>
          <div>
            <h1>Pagantes do Mês — CPFL</h1>
            <p className="page-subtitle">{criterio === 'pagamento' ? 'Pagamentos que entraram no mês escolhido (pela data do pagamento, como na arrecadação da CPFL)' : 'Parcelas pagas das faturas do mês de referência escolhido'}</p>
          </div>
        </div>
        {resultado?.dados?.length > 0 && (
          <button
            className="btn-export cpfl-pagantes-export"
            onClick={() => exportCSV(
              resultado.criterio === 'pagamento'
                ? [['Cliente', 'CPF', 'Instalação', 'Produto', 'Mês Ref.', 'Pago em', 'Valor'],
                   ...resultado.dados.map((d) => [d.cliente, d.cpf, d.instalacao, d.produto, d.mesReferencia, fmtData(d.dataPagamento), d.valor])]
                : [['Cliente', 'CPF', 'Instalação', 'Produto', 'Nº Parcela', 'Mês Ref.', 'Vencimento', 'Valor'],
                   ...resultado.dados.map((d) => [d.cliente, d.cpf, d.instalacao, d.produto, d.numeroParcela, d.mesReferencia, d.dataVencimento, d.valor])],
              `pagantes-${resultado.mes.replace('/', '-')}.csv`
            )}
          >
            <Download size={14} /> Exportar CSV
          </button>
        )}
      </div>

      {podeVerEntrada && <AbasPagantes aba={aba} setAba={setAba} />}
      <div className="rel-filters cpfl-pagantes-controls">
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { id: false, label: 'Mês Único' },
            { id: true, label: 'Todos os Pagamentos' },
          ].map((opt) => (
            <button
              key={String(opt.id)}
              type="button"
              onClick={() => setModoTodos(opt.id)}
              style={{
                padding: '9px 16px',
                borderRadius: 8,
                border: modoTodos === opt.id ? '1px solid #d97706' : '1px solid #e2e8f0',
                background: modoTodos === opt.id ? '#fffbeb' : '#fff',
                color: modoTodos === opt.id ? '#d97706' : '#64748b',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { id: 'pagamento', label: 'Data do pagamento' },
            { id: 'referencia', label: 'Mês da fatura' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              title={opt.id === 'pagamento' ? 'Dinheiro que entrou no mês (como a arrecadação da CPFL)' : 'Parcelas das faturas daquele mês de referência'}
              onClick={() => setCriterio(opt.id)}
              style={{
                padding: '9px 16px',
                borderRadius: 8,
                border: criterio === opt.id ? '1px solid #2563eb' : '1px solid #e2e8f0',
                background: criterio === opt.id ? '#eff6ff' : '#fff',
                color: criterio === opt.id ? '#2563eb' : '#64748b',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {!modoTodos && (
          <div className="cpfl-pagantes-month-picker">
              <span>{mesInputParaPt(mesInput)}</span>
              <input type="month" lang="pt-BR" aria-label="Mês de pagamento" value={mesInput} onChange={(e) => setMesInput(e.target.value)} />
            </div>
        )}
        <button
          type="button"
          onClick={consultar}
          disabled={loading}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', borderRadius: 8, border: 'none',
            background: '#d97706', color: '#fff', fontWeight: 700, fontSize: 14,
            cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1,
          }}
         className="cpfl-pagantes-consultar">
          <Search size={16} /> {loading ? 'Consultando...' : 'Consultar Pagantes'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#e11d48', padding: '12px 16px', borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {resultado && !error && (
        <>
          <div style={{ background: '#ecfdf5', border: '1px solid #10b981', color: '#047857', padding: '10px 16px', borderRadius: 8, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 14 }}>
            <CheckCircle2 size={16} /> {resultado.registros} pagamento(s) encontrado(s) em {resultado.mes}.
          </div>

          <div className="detalhe-summary-grid">
            <div className="detalhe-summary-card blue">
              <div className="summary-label"><Users size={12} style={{ marginRight: 4 }} />Clientes Pagantes</div>
              <div className="summary-value">{resultado.clientesCadastrados}</div>
            </div>
            <div className="detalhe-summary-card green">
              <div className="summary-label"><DollarSign size={12} style={{ marginRight: 4 }} />Total Arrecadado</div>
              <div className="summary-value">{formatarValor(resultado.totalArrecadado)}</div>
            </div>
            <div className="detalhe-summary-card amber">
              <div className="summary-label"><FileText size={12} style={{ marginRight: 4 }} />Registros</div>
              <div className="summary-value">{resultado.registros}</div>
            </div>
          </div>

          <div className="table-wrapper">
            <div className="table-head-title">
              Detalhamento — <span style={{ color: '#d97706' }}>⚡ Pagamentos em {resultado.mes}</span>
            </div>
            <table className="rel-table cpfl-pagantes-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Instalação</th>
                  <th>Produto</th>
                  {resultado.criterio === 'pagamento' ? (<><th>Mês ref.</th><th>Pago em</th></>) : (<><th>Nº Parcela</th><th>Vencimento</th></>)}
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {resultado.dados.map((d, i) => (
                  <tr key={`${d.identificacao}-${d.numeroParcela}-${i}`} className={i % 2 === 0 ? 'row-even' : 'row-odd'}>
                    <td className="td-client">{d.cliente}</td>
                    <td>{d.instalacao}</td>
                    <td>⚡ {d.produto}</td>
                    {resultado.criterio === 'pagamento'
                      ? (<><td>{d.mesReferencia || '—'}</td><td className="td-date">{fmtData(d.dataPagamento)}</td></>)
                      : (<><td>{d.numeroParcela || '—'}</td><td className="td-date">{d.dataVencimento || '—'}</td></>)}
                    <td className="td-amount">{formatarValor(d.valor)}</td>
                  </tr>
                ))}
                {resultado.dados.length === 0 && (
                  <tr><td colSpan={6} className="empty-row">Nenhum pagamento encontrado nesse mês</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!resultado && !loading && !error && (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
          <Calendar size={40} style={{ marginBottom: 12, opacity: 0.5 }} />
          <p>Escolha um mês e clique em "Consultar Pagantes" pra ver quem pagou.</p>
        </div>
      )}
    </div>
  );
}
